import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../services/apiClient";


const CATEGORIES = [
  { id: "Essentials", icon: "🎒", color: "bg-red-50 text-red-600" },
  { id: "Clothing", icon: "👕", color: "bg-blue-50 text-blue-600" },
  { id: "Electronics", icon: "📱", color: "bg-purple-50 text-purple-600" },
  { id: "Toiletries", icon: "🧴", color: "bg-green-50 text-green-600" },
  { id: "Documents", icon: "📄", color: "bg-yellow-50 text-yellow-600" },
];

const DEFAULT_PACKING_SUGGESTIONS = [
  { name: "Passport / ID", category: "Documents", essential: true },
  { name: "Flight Tickets", category: "Documents", essential: true },
  { name: "Wallet & Cash", category: "Essentials", essential: true },
  { name: "Phone & Charger", category: "Electronics", essential: true },
  { name: "Universal Adapter", category: "Electronics", essential: false },
  { name: "Toothbrush & Paste", category: "Toiletries", essential: true },
  { name: "Sunscreen", category: "Toiletries", essential: false },
  { name: "Underwear", category: "Clothing", essential: true },
  { name: "Socks", category: "Clothing", essential: true },
  { name: "T-shirts", category: "Clothing", essential: true },
  { name: "First Aid Kit", category: "Essentials", essential: false },
];

const PackingList = ({ onPackingUpdate }) => {
  const { travelKeyToken } = useParams();
  const token = sessionStorage.getItem("token") || localStorage.getItem("token");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [newItemCategory, setNewItemCategory] = useState("Essentials");

  // Fetch packing list on mount
  useEffect(() => {
    const fetchPacking = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/trips/${travelKeyToken}/packing`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setItems(Array.isArray(data) ? data.filter(i => i && i.name) : []);
        }
      } catch (err) {
        console.error("Failed to fetch packing list:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPacking();
  }, [travelKeyToken, token]);

  const progress = useMemo(() => {
    if (!items.length) return 0;
    const packedCount = items.filter((i) => i.packed).length;
    return Math.round((packedCount / items.length) * 100);
  }, [items]);

  const handleToggleItem = (id) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, packed: !item.packed } : item))
    );
  };

  const handleAddItem = (e) => {
    e.preventDefault();
    if (!newItemName.trim()) return;

    if (items.some(i => i.name.toLowerCase() === newItemName.trim().toLowerCase())) {
      alert("Item already exists!");
      return;
    }

    const newItem = {
      id: Date.now(),
      name: newItemName.trim(),
      category: newItemCategory,
      quantity: 1,
      packed: false,
      essential: false,
      custom: true,
    };
    setItems((prev) => [...prev, newItem]);
    setNewItemName("");
  };

  const handleRemoveItem = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleGenerate = () => {
    const generated = DEFAULT_PACKING_SUGGESTIONS.map((s, idx) => ({
      ...s,
      id: Date.now() + idx,
      quantity: 1,
      packed: false,
      custom: false,
    }));
    setItems(generated);
  };

  const handleReset = () => {
    if (window.confirm("Are you sure you want to reset your packing list? This will delete all items.")) {
      setItems([]);
      setEditMode(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/trips/${travelKeyToken}/packing`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ items }),
      });
      if (res.ok) {
        if (onPackingUpdate) onPackingUpdate(items);
        alert("Packing list saved successfully!");
        setEditMode(false);
      } else {
        alert("Failed to save packing list.");
      }
    } catch (err) {
      console.error("Save error:", err);
      alert("An error occurred while saving.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20 space-y-4">
        <div className="w-10 h-10 border-4 border-[#5b6f00] border-t-transparent rounded-full animate-spin"></div>
        <p className="text-[#6b7280] font-bold uppercase tracking-widest text-[10px]">Syncing Essentials...</p>
      </div>
    );
  }

  // 1. Generator UI
  if (items.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 shadow-sm border-2 border-dashed border-gray-100 max-w-2xl mx-auto text-center">
        <div className="w-24 h-24 bg-[#fdfcf7] rounded-full flex items-center justify-center mx-auto mb-8 transition-transform hover:scale-110">
          <span className="text-5xl opacity-40 grayscale">🧳</span>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">Pack Like a Pro</h2>
        <p className="text-[#6b7280] mb-10 max-w-sm mx-auto font-medium">
          Let’s build your checklist. We’ve curated travel essentials tailored for your upcoming mission.
        </p>
        <button
          onClick={handleGenerate}
          className="bg-[#5b6f00] text-white px-10 py-5 rounded-xl font-bold hover:bg-[#4a5a00] transition-all active:scale-95 shadow-lg shadow-[#5b6f00]/20 flex items-center gap-3 mx-auto"
        >
          Generate Smart List
        </button>
      </div>
    );
  }

  // 2. Packing List UI
  return (
    <div className="max-w-4xl mx-auto space-y-10 pb-20">
      {/* Header & Progress Card */}
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 overflow-hidden relative group">
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#5b6f00]/5 rounded-full -mr-16 -mt-16 transition-transform group-hover:scale-150 duration-700"></div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10 relative z-10">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Inventory</h2>
            <p className="text-[#6b7280] font-bold uppercase tracking-widest text-[10px] mt-1">{items.length} Elements Tracked</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              className="text-[10px] font-black uppercase tracking-widest text-[#6b7280] hover:text-red-600 transition-colors px-4"
            >
              Reset All
            </button>
            <button
              onClick={() => setEditMode(!editMode)}
              className={`px-6 py-3 rounded-xl text-sm font-bold transition-all ${editMode
                ? "bg-[#5b6f00] text-white"
                : "bg-[#fdfcf7] text-[#5b6f00] border border-[#5b6f00]/10 hover:bg-[#5b6f00]/5"
                }`}
            >
              {editMode ? "Confirm Changes" : "Modify Inventory"}
            </button>
          </div>
        </div>

        <div className="space-y-4 relative z-10">
          <div className="flex justify-between items-end">
            <span className="text-[#6b7280] font-bold uppercase tracking-widest text-[10px]">Readiness Factor</span>
            <span className="text-2xl font-black text-gray-900">{progress}%</span>
          </div>
          <div className="w-full h-3 bg-[#fdfcf7] rounded-full border border-gray-100 p-0.5 overflow-hidden">
            <motion.div
              className="h-full bg-[#5b6f00] rounded-full shadow-sm"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 1, ease: "circOut" }}
            />
          </div>
        </div>
      </div>

      {/* Categories & Items */}
      <div className="space-y-6">
        {CATEGORIES.map((category) => {
          const categoryItems = items.filter((i) => i.category === category.id);
          if (categoryItems.length === 0 && !editMode) return null;

          return (
            <div key={category.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 group">
              <div className="px-8 py-5 flex items-center gap-4 bg-[#fdfcf7]/50 border-b border-gray-50">
                <span className="text-2xl grayscale group-hover:grayscale-0 transition-all">{category.icon}</span>
                <h3 className="font-bold text-gray-900 text-lg">{category.id}</h3>
                <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-[#6b7280] opacity-60">
                  {categoryItems.length} Units
                </span>
              </div>
              <div className="divide-y divide-gray-50">
                {categoryItems.map((item) => (
                  <div
                    key={item.id}
                    className={`group flex items-center justify-between px-8 py-5 transition-colors ${item.packed ? "bg-[#fdfcf7]/30" : "hover:bg-gray-50/20"
                      }`}
                  >
                    <div className="flex items-center gap-5 flex-1">
                      <label className="relative flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="peer h-6 w-6 cursor-pointer appearance-none rounded-lg border-2 border-gray-200 transition-all checked:border-[#5b6f00] checked:bg-[#5b6f00]"
                          checked={item.packed}
                          onChange={() => handleToggleItem(item.id)}
                        />
                        <svg className="pointer-events-none absolute left-1 h-4 w-4 stroke-white opacity-0 transition-opacity peer-checked:opacity-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </label>
                      <span className={`font-bold transition-all ${item.packed ? "text-[#6b7280] line-through opacity-50" : "text-gray-700"}`}>
                        {item.name}
                        {item.essential && !item.packed && (
                          <span className="ml-3 text-[9px] bg-[#5b6f00]/10 text-[#5b6f00] px-2 py-0.5 rounded font-black uppercase tracking-tight">
                            Essential
                          </span>
                        )}
                      </span>
                    </div>
                    {editMode && (
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="p-3 text-gray-200 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}

                {editMode && (
                  <div className="p-6 bg-[#fdfcf7]/20">
                    <form
                      onSubmit={(e) => {
                        if (newItemCategory === category.id) handleAddItem(e);
                        else e.preventDefault();
                      }}
                      className="flex gap-3"
                    >
                      <input
                        type="text"
                        placeholder={`Add item to ${category.id}...`}
                        className="flex-1 bg-white border border-gray-100 rounded-xl px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/10 transition-all font-bold group-hover:border-[#5b6f00]/20"
                        value={newItemCategory === category.id ? newItemName : ""}
                        onChange={(e) => {
                          setNewItemCategory(category.id);
                          setNewItemName(e.target.value);
                        }}
                      />
                      <button
                        type="submit"
                        disabled={newItemCategory !== category.id || !newItemName.trim()}
                        className="bg-[#5b6f00] text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-[#4a5a00] disabled:opacity-20 transition-all"
                      >
                        Add
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ACTIONS */}
     {/* ACTIONS */}
{items.length > 0 && (
  <div className="max-w-4xl mx-auto pt-6 pb-10">
    <button
      onClick={handleSave}
      disabled={saving}
      className="w-full bg-gray-900 text-white py-5 rounded-2xl font-bold shadow-lg hover:bg-black active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-3"
    >
      {saving ? (
        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
      ) : (
        <>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
          </svg>
          Save Final List
        </>
      )}
    </button>
  </div>
)}
    </div>
  );
};

export default PackingList;